/**
 * AiCalibreEnrichPanel — the shared UI for the AI calibre enrichment flow.
 *
 * Owns the presentation only:
 *   - a "Reference info (optional)" textarea (ground-truth the model won't contradict)
 *   - a manual "Run enrichment" trigger
 *   - an editable correction form for every AI-returned field
 *   - "Re-run with corrections" / "Re-run fresh" / "Accept" / "Cancel" actions
 *   - loading + error states
 *
 * Each caller owns its own state container, "Run" wiring, and accept handler
 * (persistence differs per surface — create-new, update-form, etc.). This
 * component is deliberately persistence-agnostic: it surfaces the user's edits
 * via `onDraftChange` and signals intent via the callback props.
 */
import type { ReactNode } from 'react';
import { Button } from '../common/Button';
import type {
  AICalibreEnrichmentResult,
  AIProposedCalibreTier,
  CalibreData,
  CalibreTier,
  CalibreTierOverrides,
} from '../../services/firebase';
import { ALL_OVERRIDE_KEYS, isEmptyOverrideValue } from './calibreTiers';

/** User-editable copy of an enrichment result, shown in the correction form. */
export interface EditableResult {
  canonicalName: string;
  description: string;
  specs: Record<string, unknown>;
  /**
   * AI-proposed tier variants, passed through untouched (the correction form
   * doesn't edit them). Merged into the calibre's tiers[] on accept.
   */
  tiers?: AIProposedCalibreTier[];
}

/** Async state for one AI enrichment flow. */
export interface AiEnrichState {
  loading?: boolean;
  result?: AICalibreEnrichmentResult;
  /** Editable draft, seeded from `result`, that the correction form mutates. */
  draft?: EditableResult;
  error?: string;
  saving?: boolean;
}

/**
 * Tier names that are really the base calibre wearing a tier's clothes. The
 * enrichCalibre prompt forbids proposing these (the top-level spec IS the base),
 * but prompts aren't 100% reliable, so we strip them defensively client-side
 * too. Exact match after trim+lowercase — a legitimately named tier such as
 * "standard grade" is NOT caught, only the single-word synonyms below.
 */
const BASE_NAME_TIER_NAMES: ReadonlySet<string> = new Set([
  'base',
  'standard',
  'unspecified',
  'main',
  'default',
  'stock',
]);

/**
 * Drop AI-proposed base-name tiers, merging each one's overrides up into the
 * top-level `specs` (only where the spec hasn't already set that field — the
 * top-level value wins). Mutates `specs`; returns the surviving tiers.
 * Defence-in-depth mirror of the same guard in functions/src/aiEnrich.ts so a
 * base-name tier never reaches the apply-review modal as accept-able.
 */
export function dropBaseNameTiers(
  specs: Record<string, unknown>,
  tiers: AIProposedCalibreTier[],
  calibreIdentifier: string,
): AIProposedCalibreTier[] {
  const kept: AIProposedCalibreTier[] = [];
  for (const tier of tiers) {
    if (!BASE_NAME_TIER_NAMES.has(tier.name.trim().toLowerCase())) {
      kept.push(tier);
      continue;
    }
    const mergedFields: string[] = [];
    for (const [key, value] of Object.entries(tier.overrides ?? {})) {
      if (value === undefined || value === null || value === '') continue;
      const current = specs[key];
      if (current === undefined || current === null || current === '') {
        specs[key] = value;
        mergedFields.push(key);
      }
    }
    console.warn(
      '[enrichCalibre] Dropped AI-proposed base-name tier and merged overrides to top-level',
      { calibreIdentifier, droppedTierName: tier.name, mergedFields },
    );
  }
  return kept;
}

/** Seed an editable draft from a fresh enrichment result. */
export function resultToDraft(r: AICalibreEnrichmentResult): EditableResult {
  const specs: Record<string, unknown> = { ...r.specs };
  const tiers = dropBaseNameTiers(specs, r.tiers ?? [], r.canonicalName || '');
  return {
    canonicalName: r.canonicalName || '',
    description: r.description || '',
    specs,
    ...(tiers.length > 0 ? { tiers } : {}),
  };
}

/**
 * Map AI-proposed tiers (which carry no id) onto persistable `CalibreTier`
 * objects, stamping a fresh `crypto.randomUUID()` per tier and deduping by name
 * (trim+lowercase). This mirrors `CustomCalibreModal.mergeProposedTiers` so the
 * create-new surfaces (BulkCalibreEnricher, CalibreActionToolbar) persist tiers
 * identically to the manual editor.
 *
 * `tierType` is intentionally left absent — the enrichCalibre callable never
 * returns one, and absent reads as `'general'` by back-compat (see the
 * `CalibreTier` type in firebase.ts). Callers merge the result into `data.tiers`
 * only when non-empty.
 */
export function proposedTiersToCalibreTiers(
  proposed: AIProposedCalibreTier[] | undefined,
): CalibreTier[] {
  if (!proposed || proposed.length === 0) return [];
  const seen = new Set<string>();
  const out: CalibreTier[] = [];
  for (const tier of proposed) {
    const nameLower = tier.name?.trim().toLowerCase();
    if (!nameLower || seen.has(nameLower)) continue;
    seen.add(nameLower);
    out.push({
      id: crypto.randomUUID(),
      name: tier.name.trim(),
      ...(tier.description?.trim() ? { description: tier.description.trim() } : {}),
      overrides: { ...tier.overrides },
    });
  }
  return out;
}

/**
 * One user-authored decision to overlay picked fields of an AI proposal onto an
 * existing tier, from the apply dialog's per-field merge picker.
 */
export interface ProposedTierMergeChoice {
  /** The existing tier the picked fields land on. */
  targetId: string;
  /**
   * The mergeable keys the user ticked: override keys and/or `'description'`.
   * The grouped `'accuracy'` row must be expanded to its constituent override
   * keys by the caller (see `mergeableTierFields().overrideKeys`) — this
   * function honours real keys only.
   */
  fields: string[];
}

/**
 * Apply the accepted AI tier proposals to a calibre's existing `tiers[]`.
 *
 * Three routes per accepted proposal, in precedence order:
 *
 *  - MERGED via `merge` — the user opened the per-field picker and chose
 *    exactly which of the proposal's fields overlay an existing tier. Those
 *    fields OVERWRITE the tier's values, including `description`; every other
 *    field of the tier is preserved. This is the one path where the model's
 *    answer outranks the curator's, and only because the curator said so field
 *    by field.
 *
 *  - REMAPPED to an existing tier id — the model named a grade the calibre
 *    already has under another name ("RR1102-C" for an existing "C"). Its
 *    overrides merge into that tier, FILL-EMPTY per key: a key the tier already
 *    sets is left alone. The remap asserts the two names mean the same grade, not
 *    that the model's values outrank the curator's, and it carries no per-key
 *    control with which to make that finer call.
 *
 *  - Otherwise APPENDED as a new tier with a fresh id. Append-only: a proposal
 *    whose name already exists is skipped even when accepted, so an accepted
 *    proposal can never overwrite a curated tier by accident. Merging and
 *    remapping are the sanctioned ways to reach an existing one.
 *
 * Where a tier receives both a fill-empty remap and an explicit overlay, the
 * overlay wins — it is the more specific instruction. Where two overlays land
 * on one tier, the later proposal wins that key.
 *
 * Pure, and `newId` is injectable, so the merge rules are testable without a
 * DOM — they decide what lands on a curated document and were previously
 * expressible only through the modal.
 */
export function planProposedTierMerge({
  tiers,
  proposed,
  accepted,
  remap,
  merge,
  rename,
  newId = () => crypto.randomUUID(),
}: {
  tiers: CalibreTier[];
  proposed: AIProposedCalibreTier[];
  /** Indices into `proposed` the user ticked. */
  accepted: Set<number>;
  /** Proposal index → existing tier id its overrides should merge into. */
  remap?: Record<number, string | undefined>;
  /** Proposal index → the per-field overlay the user authored. */
  merge?: Record<number, ProposedTierMergeChoice | undefined>;
  /**
   * Proposal index → a new NAME for its remap target — the listed spelling of a
   * grade the tier holds under another spelling. Applies only with a remap; the
   * tier keeps its id, overrides and images. Skipped when another tier already
   * has that name.
   */
  rename?: Record<number, string | undefined>;
  newId?: () => string;
}): { tiers: CalibreTier[]; created: number; merged: number } {
  if (accepted.size === 0) return { tiers, created: 0, merged: 0 };

  const existingNames = new Set(tiers.map((t) => t.name.trim().toLowerCase()));
  const additions: CalibreTier[] = [];
  /** tierId → the override keys this batch contributes FILL-EMPTY to it. */
  const mergedInto = new Map<string, Record<string, unknown>>();
  /** tierId → the fields this batch OVERWRITES on it. */
  const overlayInto = new Map<
    string,
    { overrides: Record<string, unknown>; description?: string }
  >();
  /** tierId → its new name. */
  const renameTo = new Map<string, string>();

  proposed.forEach((tier, i) => {
    if (!accepted.has(i)) return;

    const choice = merge?.[i];
    if (choice?.targetId) {
      const target = tiers.find((t) => t.id === choice.targetId);
      if (!target) return;
      const entry = overlayInto.get(target.id) ?? { overrides: {} };
      for (const key of choice.fields) {
        if (key === 'description') {
          const description = tier.description?.trim();
          // An empty description is the proposal having nothing to say, not an
          // instruction to blank the curated one.
          if (description) entry.description = description;
          continue;
        }
        // Walk the whitelist, never the proposal's own key set: a key the model
        // invented past CalibreTierOverrides must not reach a curated document.
        if (!(key in ALL_OVERRIDE_KEYS)) continue;
        const value = (tier.overrides ?? {})[key as keyof CalibreTierOverrides];
        if (isEmptyOverrideValue(value)) continue;
        entry.overrides[key] = value;
      }
      overlayInto.set(target.id, entry);
      return;
    }

    const remapId = remap?.[i];
    if (remapId) {
      const target = tiers.find((t) => t.id === remapId);
      if (!target) return;
      const newName = rename?.[i]?.trim();
      if (
        newName &&
        newName !== target.name &&
        !tiers.some((t) => t.id !== target.id && t.name.trim().toLowerCase() === newName.toLowerCase())
      ) {
        renameTo.set(target.id, newName);
      }
      const additionsForTier = mergedInto.get(remapId) ?? {};
      for (const [key, value] of Object.entries(tier.overrides ?? {})) {
        if (value === undefined || value === null || value === '') continue;
        // Fill-empty against the tier itself AND against what an earlier
        // proposal in this same batch already contributed.
        if (target.overrides[key as keyof CalibreTierOverrides] !== undefined) continue;
        if (additionsForTier[key] !== undefined) continue;
        additionsForTier[key] = value;
      }
      mergedInto.set(remapId, additionsForTier);
      return;
    }

    const nameLower = tier.name?.trim().toLowerCase();
    if (!nameLower || existingNames.has(nameLower)) return;
    existingNames.add(nameLower);
    additions.push({
      id: newId(),
      name: tier.name.trim(),
      ...(tier.description?.trim() ? { description: tier.description.trim() } : {}),
      overrides: { ...tier.overrides },
    });
  });

  // Drop overlay fields the target already reads that way. An overlay that
  // changes nothing is not a merge, and counting it would have the toast claim
  // a write that never happened. JSON compare so the array-valued overrides
  // (balanceType, decorations, caseMaterialsAvailable) compare by content.
  const sameValue = (a: unknown, b: unknown) =>
    JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
  for (const [id, entry] of overlayInto) {
    const target = tiers.find((t) => t.id === id);
    if (!target) {
      overlayInto.delete(id);
      continue;
    }
    for (const key of Object.keys(entry.overrides)) {
      const current = target.overrides[key as keyof CalibreTierOverrides];
      if (sameValue(entry.overrides[key], current)) delete entry.overrides[key];
    }
    if (entry.description !== undefined && entry.description === target.description?.trim()) {
      delete entry.description;
    }
  }

  const hasOverlay = (id: string): boolean => {
    const entry = overlayInto.get(id);
    if (!entry) return false;
    return Object.keys(entry.overrides).length > 0 || entry.description !== undefined;
  };
  // A remap that contributed nothing new is a no-op, not a merge — same rule.
  const changedIds = new Set<string>([
    ...[...mergedInto.entries()].filter(([, o]) => Object.keys(o).length > 0).map(([id]) => id),
    ...[...overlayInto.keys()].filter(hasOverlay),
    ...renameTo.keys(),
  ]);

  if (additions.length === 0 && changedIds.size === 0) {
    return { tiers, created: 0, merged: 0 };
  }

  const nextTiers = tiers.map((t) => {
    if (!changedIds.has(t.id)) return t;
    const extra = mergedInto.get(t.id) ?? {};
    const overlay = overlayInto.get(t.id);
    // Overlay spreads LAST so an explicit per-field pick outranks a fill-empty
    // contribution from another proposal in the same batch.
    const next: CalibreTier = {
      ...t,
      overrides: { ...t.overrides, ...extra, ...(overlay?.overrides ?? {}) },
    };
    if (overlay?.description !== undefined) next.description = overlay.description;
    const newName = renameTo.get(t.id);
    if (newName) next.name = newName;
    return next;
  });

  return {
    tiers: [...nextTiers, ...additions],
    created: additions.length,
    merged: changedIds.size,
  };
}

/** Map an AI/edited specs object onto CalibreData. Covers the full set of fields
 *  the enrichment returns so user corrections to any field persist on save.
 *  Note: `manufacturer` / `brandId` are intentionally excluded — callers handle
 *  them separately (generic-movement detection + brand scoping). */
export function aiSpecsToCalibreData(specs: Record<string, unknown>): CalibreData {
  const data: CalibreData = {};
  const out = data as Record<string, unknown>;
  const str = (k: string, v: unknown) => { if (typeof v === 'string' && v.trim()) out[k] = v.trim(); };
  const num = (k: string, v: unknown) => { if (typeof v === 'number' && !Number.isNaN(v)) out[k] = v; };
  const bool = (k: string, v: unknown) => { if (typeof v === 'boolean') out[k] = v; };
  const arr = (k: string, v: unknown) => {
    if (Array.isArray(v)) {
      const a = v.filter((x): x is string => typeof x === 'string' && x.trim().length > 0).map((x) => x.trim());
      if (a.length) out[k] = a;
    }
  };

  str('movementType', specs.movementType);
  num('jewels', specs.jewels);
  num('powerReserveHours', specs.powerReserveHours);
  // Signed accuracy bounds + regulation positions — `num` accepts 0 / negatives.
  num('positionsAdjusted', specs.positionsAdjusted);
  num('accuracyLowerSecPerDay', specs.accuracyLowerSecPerDay);
  num('accuracyUpperSecPerDay', specs.accuracyUpperSecPerDay);
  // Interpretation flag for the two accuracy bounds (symmetric ± vs signed).
  bool('accuracySymmetric', specs.accuracySymmetric);
  num('frequencyVph', specs.frequencyVph);
  num('diameterMm', specs.diameter);
  num('heightMm', specs.height);
  num('liftAngleDegrees', specs.liftAngleDegrees);
  num('serviceIntervalYears', specs.serviceIntervalYears);
  num('casingDiameterMm', specs.casingDiameterMm);
  str('lignes', specs.lignes);
  str('manualWindingDirection', specs.manualWindingDirection);
  bool('hacking', specs.hacking);
  bool('manualWinding', specs.manualWinding);
  bool('quickSetDate', specs.quickSetDate);
  bool('isChronograph', specs.isChronograph);
  bool('isGMT', specs.isGMT);
  bool('isChronometer', specs.isChronometer);
  bool('isCosc', specs.isCosc);
  bool('isGlashuetteRegulation', specs.isGlashuetteRegulation);
  bool('isGenevaSeal', specs.isGenevaSeal);
  bool('isGrandSeikoStandard', specs.isGrandSeikoStandard);
  bool('isMETAS', specs.isMETAS);
  bool('isInHouse', specs.isInHouse);
  bool('isFamily', specs.isFamily);
  bool('columnWheel', specs.columnWheel);
  bool('doubleBarrel', specs.doubleBarrel);
  str('baseMovement', specs.baseMovement);
  str('windingDirection', specs.windingDirection);
  str('antiShockSystem', specs.antiShockSystem);
  str('countryOfManufacture', specs.countryOfManufacture);
  str('escapement', specs.escapement);
  str('regulatorSystem', specs.regulatorSystem);
  str('hairspring', specs.hairspring);
  str('rotorType', specs.rotorType);
  str('batteryType', specs.batteryType);
  num('batteryLifeYears', specs.batteryLifeYears);
  num('shockResistanceG', specs.shockResistanceG);
  num('antiMagneticAm', specs.antiMagneticAm);
  num('antiMagneticGauss', specs.antiMagneticGauss);
  arr('date', specs.date);
  arr('hands', specs.hands);
  arr('chronograph', specs.chronograph);
  arr('astronomical', specs.astronomical);
  arr('acoustic', specs.acoustic);
  arr('additionals', specs.additionals);
  // Decoration & finishing (v1.39.0)
  str('movementFinishing', specs.movementFinishing);
  str('movementArchitecture', specs.movementArchitecture);
  // Auto winding architecture (v1.45.0). Not gated on movement type here — the
  // prompt already tells the model to null it for non-automatic calibres, and
  // the enrich panel is a review step the admin accepts field-by-field.
  str('windingArchitecture', specs.windingArchitecture);
  num('numberOfBridges', specs.numberOfBridges);
  num('barrelCount', specs.barrelCount);
  bool('rhodiumPlated', specs.rhodiumPlated);
  bool('giltFinish', specs.giltFinish);
  // balanceType joined decorations as a multi-select in v1.91.0 — an array of
  // lookup slugs, not a scalar. `arr` drops a stray scalar rather than
  // wrapping it: the AI is prompted for an array, and a scalar here means
  // the model ignored the schema, not that it found one value.
  arr('balanceType', specs.balanceType);
  arr('decorations', specs.decorations);
  // Materials (v1.92.0). rotorMaterial is not gated on movement type here, for
  // the same reason windingArchitecture above isn't: the prompt scopes it to
  // automatic calibres and this panel is a review step the admin accepts
  // field-by-field.
  str('rotorMaterial', specs.rotorMaterial);
  str('bridgeMaterial', specs.bridgeMaterial);
  str('plateFinishing', specs.plateFinishing);
  arr('caseMaterialsAvailable', specs.caseMaterialsAvailable);
  // Skeletonization is a closed enum — only accept the three known values so a
  // stray AI string can't land a bogus value in the field.
  if (specs.skeletonization === 'full' || specs.skeletonization === 'partial' || specs.skeletonization === 'none') {
    out.skeletonization = specs.skeletonization;
  }
  return data;
}

/**
 * Decide the owning movement-manufacturer FK (`manufacturerId`) when creating a
 * calibre from a brand context. The brand is only the manufacturer when the
 * calibre is genuinely **in-house** to it — defaulting brand-as-manufacturer for
 * a generic third-party movement (ETA, Sellita, Miyota, …) would write incorrect
 * ownership data, since there the brand is merely the *user* of the movement.
 *
 * `manufacturerId` is the FK watchlock derives `manufacturerIdAncestors` from,
 * which the RN explore Manufacturer screen filters on. Returns the brand slug
 * (so the calibre shows up under the brand in RN) only when in-house is
 * established; otherwise `undefined`, leaving it for the user to set in the
 * editor rather than poisoning the data with a guess.
 *
 * Heuristic (brand context = `brandId`/`brandName`, AI signals = `specs`):
 *  1. AI names the brand itself as the maker (manufacturer matches brand) → brand.
 *  2. AI names a *different* manufacturer (ETA, Sellita, …)              → unset.
 *  3. AI flags `isInHouse: true` with no conflicting manufacturer        → brand.
 *  4. Ambiguous / no signal                                             → unset.
 * A recognised generic movement (`isGeneric`) is a hard veto regardless of (3).
 */
export function resolveBrandManufacturerId(opts: {
  specs: Record<string, unknown>;
  brandName?: string;
  brandId?: string;
  isGeneric: boolean;
}): string | undefined {
  const { specs, brandName, brandId, isGeneric } = opts;
  if (!brandId || isGeneric) return undefined;
  const manufacturer =
    typeof specs.manufacturer === 'string' ? specs.manufacturer.trim() : '';
  const slugify = (s: string) =>
    s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  const manufacturerMatchesBrand =
    manufacturer.length > 0 &&
    (manufacturer.toLowerCase() === (brandName || '').trim().toLowerCase() ||
      slugify(manufacturer) === brandId);
  if (manufacturerMatchesBrand) return brandId; // (1) brand named as maker
  if (manufacturer.length > 0) return undefined; // (2) different maker named
  if (specs.isInHouse === true) return brandId; // (3) in-house, no maker named
  return undefined; // (4) ambiguous — let the user decide
}

/** Editable input for a single spec value, typed from its runtime value. */
function SpecValueInput({
  value,
  onChange,
  disabled,
}: {
  value: unknown;
  onChange: (v: unknown) => void;
  disabled?: boolean;
}) {
  const cls = 'w-full border border-gray-300 rounded px-2 py-1 text-xs';
  if (Array.isArray(value)) {
    return (
      <input
        type="text"
        className={cls}
        disabled={disabled}
        value={value.map((v) => String(v)).join(', ')}
        placeholder="comma-separated"
        onChange={(e) =>
          onChange(
            e.target.value
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean),
          )
        }
      />
    );
  }
  if (typeof value === 'boolean') {
    return (
      <select className={cls} disabled={disabled} value={String(value)} onChange={(e) => onChange(e.target.value === 'true')}>
        <option value="true">true</option>
        <option value="false">false</option>
      </select>
    );
  }
  if (typeof value === 'number') {
    return (
      <input
        type="number"
        className={cls}
        disabled={disabled}
        value={Number.isFinite(value) ? value : ''}
        onChange={(e) => {
          if (e.target.value === '') return onChange(undefined);
          const n = Number(e.target.value);
          onChange(Number.isNaN(n) ? undefined : n);
        }}
      />
    );
  }
  return (
    <input
      type="text"
      className={cls}
      disabled={disabled}
      value={value == null ? '' : String(value)}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

export interface AiCalibreEnrichPanelProps {
  /** Async state for this calibre's enrichment flow (undefined ⇒ nothing run yet). */
  state: AiEnrichState | undefined;
  /** Reference-info free text — owned by the caller so it survives re-runs. */
  referenceInfo: string;
  onReferenceInfoChange: (v: string) => void;
  /** Trigger a fresh enrichment run. */
  onRun: () => void;
  /** The correction form mutated the draft. */
  onDraftChange: (d: EditableResult) => void;
  /** Persist / apply the current draft (caller-specific). */
  onAccept: () => void;
  /** Re-run from scratch, ignoring the current edits. */
  onRerun: () => void;
  /** Re-run, feeding the current edits back as ground-truth constraints. */
  onRerunWithCorrections: () => void;
  /** Dismiss the panel. */
  onClose: () => void;
  /** Override the accept button label (default "Accept & Save"). */
  acceptLabel?: string;
  /** Extra content rendered in the result header, e.g. caller-specific badges. */
  headerExtra?: ReactNode;
}

export function AiCalibreEnrichPanel({
  state,
  referenceInfo,
  onReferenceInfoChange,
  onRun,
  onDraftChange,
  onAccept,
  onRerun,
  onRerunWithCorrections,
  onClose,
  acceptLabel = 'Accept & Save',
  headerExtra,
}: AiCalibreEnrichPanelProps) {
  const loading = !!state?.loading;
  const saving = !!state?.saving;
  const busy = loading || saving;
  const draft = state?.draft;

  const updateSpec = (key: string, v: unknown) => {
    if (!draft) return;
    const specs = { ...draft.specs };
    if (v === undefined) delete specs[key];
    else specs[key] = v;
    onDraftChange({ ...draft, specs });
  };

  const specKeys = draft ? Object.keys(draft.specs) : [];

  return (
    <div className="text-sm space-y-4">
      {/* Reference info — always available, preserved across re-runs */}
      <div className="space-y-1">
        <label className="block text-xs font-medium text-gray-700">Reference info (optional)</label>
        <textarea
          className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
          rows={4}
          placeholder="Paste any details you already know about this calibre — sourced from Gemini, brand site, calibercorner.com, etc. The AI will treat these as ground truth and not contradict them."
          value={referenceInfo}
          onChange={(e) => onReferenceInfoChange(e.target.value)}
          disabled={busy}
        />
      </div>

      {loading && <div className="text-gray-500 italic">Asking AI…</div>}

      {state?.error && !loading && <div className="text-red-700">{state.error}</div>}

      {/* No result yet (or after an error) → manual run */}
      {!loading && !state?.result && (
        <div className="flex gap-2">
          <Button variant="primary" onClick={onRun}>
            {state?.error ? 'Try again' : 'Run enrichment'}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </div>
      )}

      {/* Result → editable correction form */}
      {!loading && state?.result && draft && (
        <div className="space-y-3">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="font-semibold">Suggestion</span>
            <span className="ml-1 px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800">
              {state.result.confidence}
            </span>
            {headerExtra}
          </div>

          <div className="space-y-1">
            <label className="block text-xs font-medium text-gray-700">Canonical name</label>
            <input
              type="text"
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm font-mono"
              value={draft.canonicalName}
              onChange={(e) => onDraftChange({ ...draft, canonicalName: e.target.value })}
              disabled={saving}
            />
          </div>

          <div className="space-y-1">
            <label className="block text-xs font-medium text-gray-700">Description</label>
            <textarea
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              rows={3}
              value={draft.description}
              onChange={(e) => onDraftChange({ ...draft, description: e.target.value })}
              disabled={saving}
            />
          </div>

          {specKeys.length > 0 && (
            <div className="space-y-1">
              <div className="text-xs font-medium text-gray-700">Fields (edit to correct, then re-run or accept)</div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-2">
                {specKeys.map((key) => (
                  <div key={key} className="flex flex-col gap-0.5">
                    <label className="text-[11px] text-gray-500 font-mono">{key}</label>
                    <SpecValueInput value={draft.specs[key]} onChange={(v) => updateSpec(key, v)} disabled={saving} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {state.result.notes && (
            <div className="text-gray-500 italic text-xs">{state.result.notes}</div>
          )}

          {state.result.sourceUrls && state.result.sourceUrls.length > 0 && (
            <div className="text-xs text-gray-600">
              <span className="font-medium">Sources: </span>
              {state.result.sourceUrls.map((u, i) => (
                <span key={u}>
                  {i > 0 && ', '}
                  <a href={u} target="_blank" rel="noreferrer" className="text-blue-600 underline break-all">
                    {u}
                  </a>
                </span>
              ))}
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
            <Button variant="success" onClick={onAccept} loading={saving}>
              {acceptLabel}
            </Button>
            <Button variant="secondary" onClick={onRerunWithCorrections} disabled={saving}>
              Re-run with corrections
            </Button>
            <Button variant="ghost" onClick={onRerun} disabled={saving}>
              Re-run fresh
            </Button>
            <Button variant="ghost" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
